import React from 'react'
import {
  Box,
  VStack,
  HStack,
  Heading,
  Text,
  Button,
  Portal,
  Checkbox,
  IconButton
} from '@chakra-ui/react'
import { FiX } from 'react-icons/fi'

interface TermsOfServiceModalProps {
  isOpen: boolean
  onClose: () => void
  onAccept: () => void
  showAcceptCheckbox?: boolean
}

export const TermsOfServiceModal: React.FC<TermsOfServiceModalProps> = ({
  isOpen,
  onClose,
  onAccept,
  showAcceptCheckbox = false
}) => {
  const [accepted, setAccepted] = React.useState(false)

  if (!isOpen) return null

  const handleAccept = () => {
    if (!showAcceptCheckbox || accepted) {
      onAccept()
    }
  }

  return (
    <Portal>
      <Box
        position="fixed"
        inset="0"
        zIndex="modal"
        display="flex"
        alignItems="center"
        justifyContent="center"
        bg="blackAlpha.600"
        onClick={onClose}
      >
        <Box
          bg="white"
          borderRadius="xl"
          shadow="2xl"
          maxW="4xl"
          w="full"
          mx={4}
          maxH="90vh"
          display="flex"
          flexDirection="column"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <Box
            p={6}
            borderBottomWidth="1px"
            borderColor="gray.200"
            flexShrink={0}
          >
            <HStack justify="space-between" align="start">
              <VStack align="start" gap={1}>
                <Heading size="xl">Terms of Service</Heading>
                <Text fontSize="sm" color="gray.600">
                  Version 2.0.0 • Last updated: 16/12/2025
                </Text>
              </VStack>
              <IconButton
                variant="ghost"
                size="sm"
                onClick={onClose}
                aria-label="Close"
                icon={<FiX size={20} />}
                borderRadius="full"
                _hover={{ bg: 'gray.100' }}
              />
            </HStack>
          </Box>

          {/* Content - Scrollable */}
          <Box
            flex={1}
            overflowY="auto"
            p={6}
            css={{
              '&::-webkit-scrollbar': {
                width: '4px',
              },
              '&::-webkit-scrollbar-track': {
                background: '#f1f1f1',
                borderRadius: '10px'
              },
              '&::-webkit-scrollbar-thumb': {
                background: '#888',
                borderRadius: '10px',
                '&:hover': {
                  background: '#555'
                }
              }
            }}
          >
            <VStack align="start" gap={8}>
              {/* Section 1 */}
              <VStack align="start" gap={3}>
                <Heading size="lg" color="gray.800">
                  1. Acceptance of Terms
                </Heading>
                <Text color="gray.700" lineHeight="tall">
                  By accessing and using Livewello, LLC ("the Service"), you accept and agree to be bound by the terms and
                  provisions of this agreement. If you do not agree to abide by these terms, please do not use this service.
                </Text>
                <Text color="gray.700" lineHeight="tall">
                  These Terms of Service constitute a legally binding agreement between you and LiveWello. By using our
                  service, you represent that you have the legal capacity to enter into this agreement.
                </Text>
              </VStack>

              {/* Section 2 */}
              <VStack align="start" gap={3}>
                <Heading size="lg" color="gray.800">
                  2. Service Description & Scope
                </Heading>
                <Text color="gray.700" lineHeight="tall">
                  Livewello, LLC is a comprehensive AI-powered wellness and genetic analysis platform that provides
                  personalized health insights and wellness guidance. Our service includes:
                </Text>
                <VStack align="start" gap={2} pl={4}>
                  <HStack align="start" gap={2}>
                    <Text color="gray.700" fontWeight="semibold">•</Text>
                    <Box>
                      <Text color="gray.700" display="inline" fontWeight="semibold">
                        GeneChat AI Assistant:
                      </Text>
                      <Text color="gray.700" display="inline">
                        {' '}Powered by OpenAI GPT-4 and Anthropic Claude, providing personalized
                        wellness guidance based on your genetic and health data
                      </Text>
                    </Box>
                  </HStack>
                  <HStack align="start" gap={2}>
                    <Text color="gray.700" fontWeight="semibold">•</Text>
                    <Box>
                      <Text color="gray.700" display="inline" fontWeight="semibold">
                        Genetic Data Processing:
                      </Text>
                      <Text color="gray.700" display="inline">
                        {' '}Analysis of genetic data from 23andMe, AncestryDNA, VCF files, and other
                        genetic testing formats
                      </Text>
                    </Box>
                  </HStack>
                  <HStack align="start" gap={2}>
                    <Text color="gray.700" fontWeight="semibold">•</Text>
                    <Box>
                      <Text color="gray.700" display="inline" fontWeight="semibold">
                        Health Data Management:
                      </Text>
                      <Text color="gray.700" display="inline">
                        {' '}Comprehensive Personal Health Record (PHR) storage, document
                        management, and wellness goal tracking
                      </Text>
                    </Box>
                  </HStack>
                  <HStack align="start" gap={2}>
                    <Text color="gray.700" fontWeight="semibold">•</Text>
                    <Box>
                      <Text color="gray.700" display="inline" fontWeight="semibold">
                        Profile Sharing & Collaboration:
                      </Text>
                      <Text color="gray.700" display="inline">
                        {' '}Role-based sharing of profiles with family members, healthcare
                        providers, and wellness coaches
                      </Text>
                    </Box>
                  </HStack>
                  <HStack align="start" gap={2}>
                    <Text color="gray.700" fontWeight="semibold">•</Text>
                    <Box>
                      <Text color="gray.700" display="inline" fontWeight="semibold">
                        Professional Features:
                      </Text>
                      <Text color="gray.700" display="inline">
                        {' '}Advanced tools for healthcare practitioners and wellness professionals (Pro
                        plan)
                      </Text>
                    </Box>
                  </HStack>
                  <HStack align="start" gap={2}>
                    <Text color="gray.700" fontWeight="semibold">•</Text>
                    <Box>
                      <Text color="gray.700" display="inline" fontWeight="semibold">
                        Wellness Goals & Accountability:
                      </Text>
                      <Text color="gray.700" display="inline">
                        {' '}Goal setting, progress tracking, and accountability partner features
                      </Text>
                    </Box>
                  </HStack>
                </VStack>
              </VStack>

              {/* Section 3 */}
              <VStack align="start" gap={3}>
                <Heading size="lg" color="gray.800">
                  3. Subscription Plans & Billing
                </Heading>
                <Text color="gray.700" lineHeight="tall" fontWeight="semibold">
                  Available Plans:
                </Text>
                <VStack align="start" gap={2} pl={4}>
                  <HStack align="start" gap={2}>
                    <Text color="gray.700" fontWeight="semibold">•</Text>
                    <Text color="gray.700">
                      <Text as="span" fontWeight="semibold">Free Plan:</Text> Limited access to basic features
                    </Text>
                  </HStack>
                  <HStack align="start" gap={2}>
                    <Text color="gray.700" fontWeight="semibold">•</Text>
                    <Text color="gray.700">
                      <Text as="span" fontWeight="semibold">Premium Plan:</Text> $19/month or $190/year
                    </Text>
                  </HStack>
                  <HStack align="start" gap={2}>
                    <Text color="gray.700" fontWeight="semibold">•</Text>
                    <Text color="gray.700">
                      <Text as="span" fontWeight="semibold">Professional Plan:</Text> $49/month or $490/year
                    </Text>
                  </HStack>
                </VStack>
              </VStack>

              {/* Section 4 */}
              <VStack align="start" gap={3}>
                <Heading size="lg" color="gray.800">
                  4. Privacy & Data Security
                </Heading>
                <Text color="gray.700" lineHeight="tall">
                  We take your privacy seriously. All genetic and health data is encrypted and stored securely.
                  We never sell your data to third parties. For complete details, please review our Privacy Policy.
                </Text>
              </VStack>

              {/* Section 5 */}
              <VStack align="start" gap={3}>
                <Heading size="lg" color="gray.800">
                  5. User Responsibilities
                </Heading>
                <Text color="gray.700" lineHeight="tall">
                  You are responsible for maintaining the confidentiality of your account credentials and for all
                  activities that occur under your account. You agree to provide accurate and complete information
                  when creating your account.
                </Text>
              </VStack>

              {/* Section 6 */}
              <VStack align="start" gap={3}>
                <Heading size="lg" color="gray.800">
                  6. Limitation of Liability
                </Heading>
                <Text color="gray.700" lineHeight="tall">
                  The Service is provided "as is" without warranties of any kind. We are not liable for any indirect,
                  incidental, or consequential damages arising from your use of the Service.
                </Text>
              </VStack>

              {/* Section 7 */}
              <VStack align="start" gap={3}>
                <Heading size="lg" color="gray.800">
                  7. Contact Information
                </Heading>
                <Text color="gray.700" lineHeight="tall">
                  For questions about these Terms of Service, please contact us at:
                </Text>
                <VStack align="start" gap={1} pl={4}>
                  <Text color="gray.700">Email: support@livewello.com</Text>
                  <Text color="gray.700">Address: Livewello, LLC</Text>
                </VStack>
              </VStack>
            </VStack>
          </Box>

          {/* Footer */}
          <Box
            p={6}
            borderTopWidth="1px"
            borderColor="gray.200"
            bg="gray.50"
            flexShrink={0}
          >
            <VStack gap={4} align="stretch">
              {showAcceptCheckbox && (
                <Checkbox
                  isChecked={accepted}
                  onChange={(e) => setAccepted(e.target.checked)}
                  colorPalette="brand"
                  size="lg"
                >
                  <Text fontSize="sm" color="gray.700">
                    I have read, understood, and agree to the Terms of Service
                  </Text>
                </Checkbox>
              )}
              <HStack gap={3}>
                <Button
                  variant="outline"
                  onClick={onClose}
                  flex={1}
                  size="lg"
                >
                  Close
                </Button>
                {showAcceptCheckbox && (
                  <Button
                    colorPalette="brand"
                    onClick={handleAccept}
                    flex={2}
                    isDisabled={!accepted}
                    size="lg"
                  >
                    Accept & Continue
                  </Button>
                )}
              </HStack>
            </VStack>
          </Box>
        </Box>
      </Box>
    </Portal>
  )
}